/**
 * Keeps deckId.selected_module_data[].used in sync with what's on the editor canvas
 * (by data-content-id). Used after undo/redo so LHS enable/disable for claims and
 * related claims matches the canvas.
 */

import { normalizeModuleContentKey } from "./claimEditorUsage";

const MATCH_ATTRIBUTES = new Set(["matchText", "matchTextVariant"]);

export function updateUsedStateFromId(data, id, action) {
  const clicked = data.find((item) => item.id === id);
  if (!clicked) return data;

  const targetElementId = clicked.elementId;
  const targetNorm = normalizeModuleContentKey(clicked.content);

  return data.map((item) => {
    let shouldBeUsed = item.used;
    const newCount = item.count || 0;

    if (item.id === id) {
      shouldBeUsed = action === "add" ? true : false;
    }

    if (
      MATCH_ATTRIBUTES.has(clicked.attribute) &&
      item.moduleId === clicked.moduleId &&
      item.elementId === targetElementId &&
      MATCH_ATTRIBUTES.has(item.attribute)
    ) {
      shouldBeUsed = action === "add" ? true : false;
    }

    if (
      targetNorm &&
      item.moduleId === clicked.moduleId &&
      normalizeModuleContentKey(item.content) === targetNorm
    ) {
      const clickedMatch = MATCH_ATTRIBUTES.has(clicked.attribute);
      const itemMatch = MATCH_ATTRIBUTES.has(item.attribute);
      if (clickedMatch && itemMatch && item.elementId !== targetElementId) {
        /* two different slices, same coincidental match copy */
      } else {
        shouldBeUsed = action === "add" ? true : false;
      }
    }

    return { ...item, used: shouldBeUsed, count: newCount };
  });
}

/**
 * Rebuilds `used` flags from all [data-content-id] elements present in the canvas iframe.
 */
export function syncSelectedModuleUsedFromCanvas(editor, appData) {
  if (!appData?.setDeckId || !editor) return;

  const idsOnCanvas = new Set();
  const frame = editor.Canvas?.getFrameEl?.();
  const doc = frame?.contentDocument;
  if (doc) {
    doc.querySelectorAll("[data-content-id]").forEach((el) => {
      const id = el.getAttribute("data-content-id");
      if (id) idsOnCanvas.add(id);
    });
  }

  // Undo/redo can restore nodes in model tree before iframe DOM fully updates.
  try {
    const wrapper = editor.getWrapper?.();
    if (wrapper?.find) {
      const comps = wrapper.find("[data-content-id]");
      const each = (c) => {
        const id = c?.getAttributes?.()?.["data-content-id"];
        if (id) idsOnCanvas.add(id);
      };
      if (comps?.forEach) {
        comps.forEach(each);
      } else if (comps?.models?.forEach) {
        comps.models.forEach(each);
      }
    }
  } catch (_) {
    /* ignore */
  }

  appData.setDeckId((prev) => {
    const modules = prev?.selected_module_data;
    if (!Array.isArray(modules) || modules.length === 0) return prev;

    let data = modules.map((item) => ({ ...item, used: false }));
    idsOnCanvas.forEach((id) => {
      data = updateUsedStateFromId(data, id, "add");
    });

    const changed = data.some((m, i) => m.used !== modules[i]?.used);
    if (!changed) return prev;

    return {
      ...prev,
      selected_module_data: data,
    };
  });
}
